"use strict";
/**
 * DOM traversal and element finding utilities for ESI banner hydration
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.findUnhydratedBannersIn = exports.findBannerToHydrate = exports.findFirstUnhydratedBanner = exports.findEsiRootFromScript = exports.findBannerDataScript = void 0;
const constants_1 = require("../constants");
/**
 * Find the banner data script element relative to a root element.
 * Searches backwards from the root element for a script tag with data-esi-data attribute.
 *
 * ESI Context:
 * - In production (embedded), scripts are positioned before their banner root
 * - In isolation (testing), browsers may move scripts to <head>
 *
 * @param rootElem - The banner root element
 * @returns The script element containing banner data, or null if not found
 */
const findBannerDataScript = (rootElem) => {
    // Primary approach: Look for script with data-esi-data attribute before the root element
    let sibling = rootElem.previousElementSibling;
    while (sibling) {
        if (sibling.tagName === "SCRIPT" && sibling.hasAttribute("data-esi-data")) {
            return sibling;
        }
        sibling = sibling.previousElementSibling;
    }
    // Fallback: When loaded in isolation, browser moves scripts to <head>
    // This maintains compatibility with both embedded (production) and isolated (testing) scenarios
    const scriptInHead = document.querySelector("head script[data-esi-data]");
    if (scriptInHead) {
        return scriptInHead;
    }
    return null;
};
exports.findBannerDataScript = findBannerDataScript;
/**
 * Find the banner root element that comes after a given script tag.
 * This allows each script execution to find its own banner in its ESI fragment.
 *
 * ESI Isolation Strategy:
 * Each ESI fragment includes its own script, so this function helps maintain
 * the one-to-one relationship between script and banner.
 *
 * @param scriptElem - The script element to search from
 * @returns The banner root element, or null if not found
 */
const findEsiRootFromScript = (scriptElem) => {
    if (!scriptElem)
        return null;
    // Traverse forward from the script tag to find the banner root div
    let sibling = scriptElem.nextElementSibling;
    while (sibling) {
        if (sibling.id === constants_1.BANNER_ROOT_ELEM_ID) {
            return sibling;
        }
        sibling = sibling.nextElementSibling;
    }
    return null;
};
exports.findEsiRootFromScript = findEsiRootFromScript;
/**
 * Find the first unhydrated banner element in the document.
 * Used as a fallback when script-relative approach fails.
 *
 * @returns The first banner element without data-hydrated attribute, or null
 */
const findFirstUnhydratedBanner = () => {
    const allBanners = document.querySelectorAll(`#${constants_1.BANNER_ROOT_ELEM_ID}`);
    return (Array.from(allBanners).find((elem) => !elem.hasAttribute("data-hydrated")) || null);
};
exports.findFirstUnhydratedBanner = findFirstUnhydratedBanner;
/**
 * Find the banner element to hydrate based on the current script context.
 *
 * This function implements a fallback strategy:
 * 1. If currentScript is available, find the banner after that script
 * 2. If script-relative approach fails, find the first unhydrated banner
 *
 * @param currentScript - The currently executing script element (from document.currentScript)
 * @returns The banner element to hydrate, or null if none found
 */
const findBannerToHydrate = (currentScript) => {
    if (currentScript) {
        // Primary approach: Find the banner that belongs to the currently executing script
        const bannerFromScript = (0, exports.findEsiRootFromScript)(currentScript);
        if (bannerFromScript) {
            return bannerFromScript;
        }
    }
    // Fallback: Find first unhydrated banner
    // This handles cases where:
    // - currentScript is null (bundlers, module scripts)
    // - Script is positioned after the banner
    return (0, exports.findFirstUnhydratedBanner)();
};
exports.findBannerToHydrate = findBannerToHydrate;
/**
 * Find all unhydrated banner elements within a given element or its descendants.
 *
 * @param element - The element to search within
 * @returns Array of unhydrated banner elements
 */
const findUnhydratedBannersIn = (element) => {
    const banners = [];
    // Check if the element itself is an unhydrated banner
    if (element.id === constants_1.BANNER_ROOT_ELEM_ID &&
        !element.hasAttribute("data-hydrated")) {
        banners.push(element);
    }
    // Check for banner roots within the element
    if (element.querySelectorAll) {
        const innerBanners = element.querySelectorAll(`#${constants_1.BANNER_ROOT_ELEM_ID}:not([data-hydrated])`);
        banners.push(...Array.from(innerBanners));
    }
    return banners;
};
exports.findUnhydratedBannersIn = findUnhydratedBannersIn;
